/* cords2-card.js - the finish bar on a product card.

   TWO CARDS, ONE BEHAVIOUR WITH A SWITCH. The Home range card previews: point
   at a finish and the picture changes while the pointer is there, let go and
   it returns. The Shop listing card selects: a click sticks, and the card's
   own link and its Shop now button follow the chosen finish through to the
   product page. A card opts into the second by carrying data-c2-pick.

   A PHONE HAS NO POINTER, so it got neither. 16 October 2026, after Viktor
   said the colour selector links straight through without previewing.

   Preview is built on pointerenter, which a finger cannot produce in any
   lasting way, and only a selecting card was intercepting the click. So on
   the Home band every segment behaved as what it has always literally been:
   a link to that variant. Tapping Sand did not show you Sand, it took you to
   a different page.

   Any device without hover therefore gets the selecting behaviour, whether
   or not the card asked for it. That is the right default rather than a
   workaround: a preview that returns when you leave needs somewhere to
   leave to, and a touch screen has nowhere. A tap has to mean choose.

   AND THE HOVER HANDLERS COME OFF ENTIRELY THERE, which is the second half
   of the same fix, after Viktor said the picture flickered. It was not the
   painter: it was the order the browser fires things in.

   When a finger lifts, the pointer ceases to exist, so the specification has
   the browser fire pointerout and pointerleave BEFORE it fires click. One
   tap was running three picture changes:

     pointerenter -> paint(new)
     pointerleave -> reset(), and nothing is chosen yet, so clear(base)
     click        -> choose() -> paint(new)

   New, old, new. The middle step is clear(), which assigns src directly with
   no decode because it is restoring a picture the browser already has, so it
   lands instantly and the eye catches it. Decoding before the swap, which is
   what stopped the flicker on a pointer in October, cannot help: the flicker
   was never a half-painted image, it was a correct image painted twice over
   a wrong one.

   So on a coarse pointer the enter and leave handlers are simply not bound.
   Focus still paints, because focus is how a keyboard arrives and how a tap
   lands on a link anyway, and the swap guard makes the click's repaint a
   no-op. One tap, one change. The side benefit is that dragging a finger
   down the page over a finish bar no longer repaints cards on the way past.

   Every chip is a real link to that variant, so with JavaScript off a click
   is a page load to the product in that finish and the bar is still usable
   from the keyboard. This file only intercepts the plain left click; a
   modified click, a middle click and the context menu are left alone, so
   open-in-new-tab still does what it says.

   Preview and selection share one painter. Two things differ. On the way out
   a previewing card returns to the product's own picture while a selecting
   card returns to the finish that was chosen. And the line under the name:
   the Home card REPLACES the kind with the finish, because the preview is
   momentary and the kind comes straight back; a selection is not momentary,
   so replacing it there would leave the card permanently unable to say what
   the product is. It appends instead: `Power extender · Sand`.

   THE PICTURE IS NOT CLEARED BEFORE IT IS REPLACED. 15 October 2026, after
   the first flicker report, this one on a pointer. This painter used to blank
   srcset and then assign src, which is the only way to make src win over a
   srcset, but it also drops what the element is showing: on a cold cache the
   card went empty and the new finish faded up out of nothing. The new picture
   is decoded off-screen first and only put in once it is ready, so the old
   finish stays until the new one can replace it in one frame. The marker and
   the label still move immediately, because those are instant and are what
   says the tap registered.

   A TOKEN GUARDS AGAINST A LATE ARRIVAL. Running a finger along five chips
   starts five decodes, and they do not finish in order. Each swap claims a
   number and a decode that is no longer the newest is dropped, so the card
   cannot settle on a finish the visitor has already moved past. clear() bumps
   it too, or a slow decode would overwrite the picture it just restored.

   THE PRODUCT PAGE CAN CHOOSE FOR EVERY CARD, 25 October 2026. On a product
   page the range band follows the finish in the buy panel. The section draws
   each card in the page's finish with that segment marked data-c2-picked, so
   it starts chosen here; and cords2-pdp.js fires c2:finish with the new
   variant's options whenever the shopper picks another finish, and every card
   that has a segment by that name chooses it. A choice made by the page keeps
   the card's own kind line ("Power extender"), because the picture already
   shows the colour and five cards all saying "Sand" says nothing. A finish
   the visitor picks on the card itself still behaves exactly as before. */
(function () {
  if (window.__c2CardBound) return;
  window.__c2CardBound = true;

  /* Asked once. A device does not grow a mouse mid-session, and reading it
     per card would be the same answer six times over. */
  var COARSE = !!(window.matchMedia && window.matchMedia('(hover: none)').matches);

  function bind(card) {
    var shot = card.querySelector('.c2-card__shot');
    var kind = card.querySelector('[data-c2-kind]');
    var bar = card.querySelector('.c2-swatches, .c2-swatchrow');
    var swatches = card.querySelectorAll('[data-c2-swatch], [data-c2-finish]');
    if (!shot || !swatches.length) return;

    var sticky = card.hasAttribute('data-c2-pick') || COARSE;
    var links = card.querySelectorAll('[data-c2-href]');
    var baseSrc = shot.getAttribute('src');
    var baseSet = shot.getAttribute('srcset') || '';
    var baseKind = kind ? kind.textContent : '';
    var picked = null;
    var fromPage = false;
    var warmed = false;
    var token = 0;

    function warm() {
      if (warmed) return;
      warmed = true;
      Array.prototype.forEach.call(swatches, function (s) {
        var u = s.getAttribute('data-shot');
        if (u) { var i = new Image(); i.src = u; }
      });
    }

    function label(name) {
      if (!name) return baseKind;
      if (!sticky) return name;
      return baseKind ? baseKind + ' · ' + name : name;
    }

    /* Decode first, then put it in. Nothing is cleared in between.

       The early return is what makes the click's repaint free: focus has
       already painted the finish by the time click chooses it, and without
       this that second paint would decode and re-assign the picture already
       on screen. */
    function swap(u) {
      if (!u) return;
      if (shot.getAttribute('src') === u && !shot.getAttribute('srcset')) return;
      var mine = ++token;
      var pre = new Image();
      function put() {
        if (mine !== token) return;
        /* srcset would otherwise win over the src we are about to set */
        shot.setAttribute('srcset', '');
        shot.setAttribute('src', u);
      }
      pre.src = u;
      if (pre.decode) { pre.decode().then(put, put); }
      else if (pre.complete) { put(); }
      else { pre.onload = put; pre.onerror = put; }
    }

    function paint(s) {
      card.classList.add('c2-card--picking');
      Array.prototype.forEach.call(swatches, function (o) { o.removeAttribute('data-c2-on'); });
      s.setAttribute('data-c2-on', '');
      if (kind) kind.textContent = (fromPage && s === picked) ? baseKind : label(s.getAttribute('data-name'));
      swap(s.getAttribute('data-shot'));
    }

    function clear() {
      token++;
      card.classList.remove('c2-card--picking');
      Array.prototype.forEach.call(swatches, function (o) { o.removeAttribute('data-c2-on'); });
      if (kind) kind.textContent = baseKind;
      shot.setAttribute('src', baseSrc);
      if (baseSet) shot.setAttribute('srcset', baseSet);
    }

    /* Leaving the bar returns to the chosen finish, or to the product's own
       picture when nothing has been chosen. */
    function reset() {
      if (picked) { paint(picked); } else { clear(); }
    }

    function choose(s) {
      picked = s;
      paint(s);
      Array.prototype.forEach.call(swatches, function (o) {
        o.removeAttribute('data-c2-picked');
        o.removeAttribute('aria-current');
      });
      s.setAttribute('data-c2-picked', '');
      s.setAttribute('aria-current', 'true');
      var href = s.getAttribute('href');
      if (!href) return;
      /* The card's picture and its button now lead to this finish. */
      Array.prototype.forEach.call(links, function (l) { l.setAttribute('href', href); });
    }

    if (COARSE) {
      /* A finger never enters the card, so the first tap would otherwise pay
         for its own decode with nothing warmed. */
      card.addEventListener('touchstart', warm, { passive: true });
    } else {
      card.addEventListener('pointerenter', warm);
    }

    Array.prototype.forEach.call(swatches, function (s) {
      /* Not bound on a touch screen: see the note at the top. This is the
         first of the three paints a single tap used to run. */
      if (!COARSE) s.addEventListener('pointerenter', function () { paint(s); });
      s.addEventListener('focus', function () { warm(); paint(s); });

      if (!sticky) return;

      s.addEventListener('click', function (e) {
        /* cmd, ctrl, shift, alt or anything but the primary button is the
           visitor asking the browser for the link, not for the card. */
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        if (typeof e.button === 'number' && e.button !== 0) return;
        e.preventDefault();
        fromPage = false;
        choose(s);
      });
    });

    /* Drawn chosen by the section, on a product page. */
    var first = card.querySelector('[data-c2-picked]');
    if (first) { picked = first; fromPage = true; }

    /* Called on c2:finish with the variant's option values. */
    card.__c2Finish = function (names) {
      for (var i = 0; i < swatches.length; i++) {
        if (names.indexOf(swatches[i].getAttribute('data-name')) > -1) {
          fromPage = true;
          choose(swatches[i]);
          return;
        }
      }
    };

    /* The second of the three. On a touch screen pointerleave arrives
       between the tap and the click, with nothing chosen yet, so reset()
       meant clear() and the picture snapped back to the product's own shot
       for a frame. Nothing to leave to on a phone, so nothing to bind. */
    if (!COARSE) {
      if (bar) bar.addEventListener('pointerleave', reset);
      card.addEventListener('pointerleave', reset);
    }
    card.addEventListener('focusout', function (e) {
      if (!card.contains(e.relatedTarget)) reset();
    });
  }

  Array.prototype.forEach.call(document.querySelectorAll('[data-c2-card]'), bind);

  /* THE LISTING'S COLOUR FILTER, 26 October 2026. The listing section draws
     the chosen colour itself when Shopify reports it as an active filter.
     When it does not (the colour filter is not switched on in Search &
     Discovery, so Shopify ignores the parameter), this reads the colour from
     the address and does the same here: every card that offers it chooses
     it, keeping its kind line as the product page's choice does, and the
     rail marks it and turns its link into the way back. */
  (function () {
    var want = null;
    try { want = new URLSearchParams(location.search).get('filter.v.option.color'); } catch (e) {}
    var rail = document.querySelector('[data-c2-colors]');
    if (!want || !rail || rail.querySelector('[data-c2-color][aria-current]:not([data-c2-color=""])')) return;
    Array.prototype.forEach.call(document.querySelectorAll('[data-c2-card][data-c2-pick]'), function (card) {
      if (!card.querySelector('[data-c2-picked]') && card.__c2Finish) card.__c2Finish([want]);
    });
    var base = rail.getAttribute('data-base') || location.pathname;
    Array.prototype.forEach.call(rail.querySelectorAll('[data-c2-color]'), function (a) {
      var on = a.getAttribute('data-c2-color') === want;
      var label = a.querySelector('span[style*="flex-grow"]');
      if (on) { a.setAttribute('aria-current', 'true'); a.setAttribute('href', base); }
      else { a.removeAttribute('aria-current'); }
      if (label) label.classList.toggle('c2-underline', on);
    });
  })();

  document.addEventListener('c2:finish', function (e) {
    var names = (e.detail && e.detail.options) || [];
    Array.prototype.forEach.call(document.querySelectorAll('[data-c2-card]'), function (card) {
      if (card.__c2Finish) card.__c2Finish(names);
    });
  });
})();